import { DateTime } from 'luxon'
import { AllEngagementRecordings, Camera, CameraRecording } from '../../../../../api/models'
import { type EngagementRecordings, type EngagementClip } from '../types'

/**
 * Flatten one camera's API recording (which nests N videos) into per-clip records. Also the converter for
 * the per-camera refresh endpoint behind useCameraRecording, which returns the same CameraRecording shape
 * (museum still 501s it as of 2026-08-05 - wire it through here once implemented).
 *
 * @param engagementAccess the engagement-level access flags; a clip is viewable only when BOTH the
 *   engagement and its camera allow it.
 */
export const cameraRecordingToClips = (
  cameraId: string,
  recording: CameraRecording,
  engagementAccess: Pick<AllEngagementRecordings, 'accessAllowed' | 'disallowedReason'>
): EngagementClip[] =>
  (recording.videos || []).filter(Boolean).map(video => ({
    startTime: video.startTime,
    endTime: video.endTime,
    links: video.links,
    agentViewStart: video.agentViewStart,
    cameraId,
    // Deterministic so recordingId survives repeated calls with the same data - InspectPlayer uses it as
    // a React key and as a lookup key into per-camera "active clip" state.
    recordingId: `${cameraId}:${video.startTime}`,
    accessAllowed: engagementAccess.accessAllowed && recording.accessAllowed,
    disallowedReason: recording.disallowedReason ?? engagementAccess.disallowedReason
  }))

/**
 * Flatten the engagement recordings response into per-clip records grouped by camera, plus the global
 * earliest/latest footage bounds that become the shared timeline axis.
 */
export const getEngagementRecordings = (recordings: AllEngagementRecordings): EngagementRecordings => {
  const recordingsByCameraId: Map<string, EngagementClip[]> = new Map()
  let earliestStartTimeMs: number = Number.POSITIVE_INFINITY
  let latestEndTimeMs: number = 0
  let earliestStartTimeISO = ''
  let latestEndTimeISO = ''

  const deniedReasonByCameraId: Map<string, CameraRecording['disallowedReason']> = new Map()

  for (const [cameraId, recording] of Object.entries(recordings.recordings ?? {})) {
    const clips = cameraRecordingToClips(cameraId, recording, recordings)
    if (!recording.accessAllowed) {
      deniedReasonByCameraId.set(cameraId, recording.disallowedReason ?? recordings.disallowedReason)
    }
    for (const clip of clips) {
      const startMillis = DateTime.fromISO(clip.startTime).toMillis()
      const endMillis = DateTime.fromISO(clip.endTime).toMillis()

      if (startMillis < earliestStartTimeMs) {
        earliestStartTimeMs = startMillis
        earliestStartTimeISO = clip.startTime
      }
      if (endMillis > latestEndTimeMs) {
        latestEndTimeMs = endMillis
        latestEndTimeISO = clip.endTime
      }
    }
    recordingsByCameraId.set(cameraId, clips)
  }

  return {
    recordingsByCameraId,
    earliestStartTime: earliestStartTimeISO,
    latestEndTime: latestEndTimeISO,
    accessAllowed: recordings.accessAllowed,
    disallowedReason: recordings.disallowedReason,
    deniedReasonByCameraId
  }
}

// Caps a recordings window's latestEndTime at the engagement end so the timeline never extends past it.
// Only the window bound moves; the per-camera clips are untouched (playback truncation handles those, and
// the shortened window clamps any post-end clip/viewed segment to zero width when rendered).
export const capRecordingsAtEngagementEnd = (recordings: EngagementRecordings, engagementEndEpoch: number | null): EngagementRecordings => {
  if (engagementEndEpoch == null) return recordings
  const latestMs = DateTime.fromISO(recordings.latestEndTime).toMillis()
  if (latestMs <= engagementEndEpoch) return recordings
  return { ...recordings, latestEndTime: DateTime.fromMillis(engagementEndEpoch).toISO() ?? recordings.latestEndTime }
}

/**
 * Shrink clips to their actual archived media duration: endTime becomes min(endTime, startTime +
 * duration). Only ever shrinks - a probe proves footage stops early, never that more exists. Recomputes
 * the axis bounds since the latest end can move. Clips without an entry keep their museum window.
 */
export const applyClipMediaDurations = (
  recordings: EngagementRecordings,
  mediaDurationSecondsByRecordingId: Map<string, number>
): EngagementRecordings => {
  if (mediaDurationSecondsByRecordingId.size === 0) return recordings

  const recordingsByCameraId = new Map<string, EngagementClip[]>()
  let earliestStartTimeMs: number = Number.POSITIVE_INFINITY
  let latestEndTimeMs: number = 0
  let earliestStartTimeISO = ''
  let latestEndTimeISO = ''

  for (const [cameraId, clips] of recordings.recordingsByCameraId) {
    const correctedClips = clips.map(clip => {
      const durationSeconds = mediaDurationSecondsByRecordingId.get(clip.recordingId)
      if (durationSeconds === undefined || durationSeconds <= 0) return clip
      const windowEndMs = DateTime.fromISO(clip.endTime).toMillis()
      const mediaEndMs = DateTime.fromISO(clip.startTime).toMillis() + durationSeconds * 1000
      if (mediaEndMs >= windowEndMs) return clip
      return { ...clip, endTime: DateTime.fromMillis(mediaEndMs).toISO() ?? clip.endTime }
    })

    for (const clip of correctedClips) {
      const startMillis = DateTime.fromISO(clip.startTime).toMillis()
      const endMillis = DateTime.fromISO(clip.endTime).toMillis()
      if (startMillis < earliestStartTimeMs) {
        earliestStartTimeMs = startMillis
        earliestStartTimeISO = clip.startTime
      }
      if (endMillis > latestEndTimeMs) {
        latestEndTimeMs = endMillis
        latestEndTimeISO = clip.endTime
      }
    }
    recordingsByCameraId.set(cameraId, correctedClips)
  }

  return {
    ...recordings,
    recordingsByCameraId,
    earliestStartTime: earliestStartTimeISO,
    latestEndTime: latestEndTimeISO
  }
}

export const findMatchingCamera = (uuid: string, cameras: Camera[]): Camera | undefined => {
  return cameras.find(camera => camera.uuid === uuid)
}

/**
 * Cameras in the order Inspect presents them: the engagement's triggering camera first (it's what the
 * engagement is about), then the rest in the order museum returned them - which does NOT itself put the
 * triggering camera first. Both the timeline rows and the camera selected on load go through this, so
 * "the first camera" means the same thing in the UI and in playback.
 *
 * @param triggeringCameraId undefined (engagement not loaded / camera has no recordings) leaves museum's
 *   order untouched.
 */
export const getCameraEntriesInDisplayOrder = (
  recordingsByCameraId: Map<string, EngagementClip[]>,
  triggeringCameraId: string | undefined,
  allCameras: Camera[] = []
): Array<[string, EngagementClip[]]> => {
  return Array.from(recordingsByCameraId.entries()).sort(([cameraIdA], [cameraIdB]) => {
    const isCameraATrigger = cameraIdA === triggeringCameraId
    const isCameraBTrigger = cameraIdB === triggeringCameraId

    if (isCameraATrigger && !isCameraBTrigger) {
      return -1
    }
    if (!isCameraATrigger && isCameraBTrigger) {
      return 1
    }
    const cameraAname = findMatchingCamera(cameraIdA, allCameras)?.name ?? ''
    const cameraBname = findMatchingCamera(cameraIdB, allCameras)?.name ?? ''
    return cameraAname.localeCompare(cameraBname)
  })
}
